import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Circle, Clock3, Loader2, RotateCcw } from "lucide-react";
import { fmtDT } from "../../lib/date";

const money = value => "$" + Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const normalizedStatus = status => ["finance_approved", "check_issued"].includes(status) ? "finance_pending" : status;
const requestTime = request => Date.parse(request.createdAt || request.submittedAt || "") || 0;

/** 一份合同只读取一次项目申请，所有阶段复用；合同刷新后重新读取审批状态。 */
export function usePortalPaymentRequests({ api, sc, enabled = true }) {
  const key = `${sc?.projectId || ""}:${sc?.id || ""}`;
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState({ key: "", requests: [], loading: false, error: null });
  const onRetry = useCallback(() => setRetry(value => value + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    if (!sc?.id || !sc?.projectId) {
      setState({ key, requests: [], loading: false, error: "missing_project" });
      return undefined;
    }
    setState({ key, requests: [], loading: true, error: null });
    api.get(`/projects/${encodeURIComponent(sc.projectId)}/payment-requests`)
      .then(({ data }) => {
        if (!Array.isArray(data)) throw new Error("Invalid payment request response");
        if (active) setState({ key, requests: data.filter(request => request?.subcontractId === sc.id), loading: false, error: null });
      })
      .catch(() => {
        if (active) setState({ key, requests: [], loading: false, error: "load_failed" });
      });
    return () => { active = false; };
  }, [api, sc, key, enabled, retry]);

  const current = state.key === key ? state : { requests: [], loading: enabled, error: null };
  return { requests: current.requests, loading: current.loading, error: current.error, onRetry };
}

/** 拒绝会清空阶段 requestId，因此必须按合同和阶段查最新申请，不能只用 requestId。 */
export function getPortalStagePaymentRequests(sc, milestone, requests) {
  if (!sc?.id || !milestone?.id) return [];
  return (Array.isArray(requests) ? requests : [])
    .filter(request => request?.subcontractId === sc.id && request?.milestoneId === milestone.id)
    .sort((a, b) => requestTime(b) - requestTime(a));
}

function statusLabel(status, zh) {
  return ({
    submitted: zh ? "等待项目经理验收" : "Awaiting PM acceptance",
    pm_approved: zh ? "等待工程经理复核" : "Awaiting engineering review",
    finance_pending: zh ? "等待财务付款" : "Awaiting finance payment",
    paid: zh ? "已付款" : "Paid",
    rejected: zh ? "申请已驳回" : "Request rejected",
    void: zh ? "付款已作废" : "Payment voided",
  })[normalizedStatus(status)] || (zh ? "付款状态待确认" : "Payment status unavailable");
}

function actionLabel(action, zh) {
  return ({
    submit: zh ? "提交申请" : "Request submitted",
    pm_approve: zh ? "项目经理验收通过" : "PM acceptance approved",
    eng_review: zh ? "工程经理复核通过" : "Engineering review approved",
    finance_approve: zh ? "财务审核通过" : "Finance approved",
    issue_check: zh ? "支票已开立" : "Check issued",
    print_confirm: zh ? "财务确认付款" : "Payment confirmed",
    reject: zh ? "申请驳回" : "Request rejected",
    void: zh ? "付款作废" : "Payment voided",
    admin_edit: zh ? "付款记录已更正" : "Payment record corrected",
  })[action] || (zh ? "记录更新" : "Record updated");
}

const rejectReason = request => request.rejectReason || [...(request.history || [])].reverse().find(item => item.action === "reject")?.note;

/**
 * 只展示父级共享的请求数据，不在每个阶段重复请求。
 * props: sc, m, zh, requests, loading, error, onRetry。
 */
export default function PortalStagePaymentStatus({ sc, m, zh, requests, loading, error, onRetry }) {
  const testid = `portal-stage-payment-${m.id}`;
  if (loading) return <div data-testid={testid} role="status" className="flex items-center gap-2 py-2 text-xs text-slate-500"><Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />{zh ? "正在读取付款进度…" : "Loading payment progress…"}</div>;
  if (error) return <div data-testid={`${testid}-error`} role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
    <span className="inline-flex items-center gap-1.5"><AlertCircle aria-hidden="true" className="h-4 w-4 shrink-0" />{zh ? "付款进度暂时无法读取" : "Payment progress could not be loaded"}</span>
    <button type="button" onClick={onRetry} data-testid={`${testid}-retry`} className="inline-flex min-h-11 items-center gap-1.5 rounded-md px-2 font-semibold hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"><RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />{zh ? "重试" : "Retry"}</button>
  </div>;

  const stageRequests = getPortalStagePaymentRequests(sc, m, requests);
  const latest = stageRequests[0];
  if (!latest) return <p data-testid={`${testid}-empty`} className="py-1 text-xs text-slate-500">{m.status === "paid" ? (zh ? "已付款，暂无详细审批记录。" : "Paid. Detailed approval records are unavailable.") : m.status === "requested" || m.requestId ? (zh ? "已提交申请，暂无详细审批记录。" : "Request submitted. Detailed approval records are unavailable.") : (zh ? "尚未申请进度款" : "Payment has not been requested")}</p>;

  const status = normalizedStatus(latest.status);
  const interrupted = status === "rejected" || status === "void";
  const step = { submitted: 1, pm_approved: 2, finance_pending: 3, paid: 4 }[status];
  const StatusIcon = interrupted ? AlertCircle : status === "paid" ? CheckCircle2 : Clock3;
  const color = interrupted ? "text-red-700" : status === "paid" ? "text-emerald-700" : "text-brand-700";
  const steps = zh ? ["提交申请", "项目经理验收", "工程经理复核", "财务付款"] : ["Submitted", "PM acceptance", "Engineering review", "Finance payment"];
  const reason = status === "rejected" ? rejectReason(latest) : latest.voidReason;
  const hasHistory = stageRequests.length > 1 || stageRequests.some(request => request.history?.length);
  // 旧申请的付款时间只在确认交付历史中；现金/信用卡编号也不能标成支票号。
  const paidAt = latest.paidAt || [...(latest.history || [])].reverse().find(entry => entry.action === "print_confirm")?.at;
  const referenceLabel = latest.paymentMethod && latest.paymentMethod !== "check" ? (zh ? "付款参考号：" : "Reference: ") : (zh ? "支票号：" : "Check: ");

  return <div data-testid={testid} data-status={status || "unknown"} className="space-y-2">
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm">
      <span className={`inline-flex items-center gap-1.5 font-semibold ${color}`}><StatusIcon aria-hidden="true" className="h-4 w-4 shrink-0" />{statusLabel(status, zh)}</span>
      <span data-testid={`${testid}-amount`} className="font-semibold tabular-nums text-slate-800">{money(latest.amount)}</span>
    </div>
    {step != null && <ol aria-label={zh ? "付款审批进度" : "Payment approval progress"} className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs sm:flex sm:flex-wrap sm:gap-x-4">
      {steps.map((label, index) => {
        const completed = index < step;
        const current = index === step;
        const StepIcon = completed ? CheckCircle2 : current ? Clock3 : Circle;
        return <li key={label} aria-current={current ? "step" : undefined} className={`flex min-w-0 items-start gap-1.5 ${completed ? "text-emerald-700" : current ? "font-semibold text-brand-700" : "text-slate-400"}`}><StepIcon aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{label}</span></li>;
      })}
    </ol>}
    {interrupted && <p data-testid={`${testid}-reason`} className="break-words rounded-lg bg-red-50 px-3 py-2 text-xs leading-relaxed text-red-700">{zh ? "原因：" : "Reason: "}{reason || (zh ? "请联系项目经理确认。" : "Contact the project manager for details.")}</p>}
    <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
      {(latest.submittedAt || latest.createdAt) && <span>{zh ? "申请时间：" : "Submitted: "}{fmtDT(latest.submittedAt || latest.createdAt)}</span>}
      {status === "paid" && paidAt && <span>{zh ? "付款时间：" : "Paid: "}{fmtDT(paidAt)}</span>}
      {status === "paid" && latest.checkNo && <span>{referenceLabel}{latest.checkNo}</span>}
    </p>
    {hasHistory && <details data-testid={`${testid}-history`} className="text-xs">
      <summary className="w-fit cursor-pointer rounded-md py-1 text-slate-500 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">{zh ? "申请与审批记录" : "Request and approval history"}</summary>
      <div className="mt-2 space-y-3 border-t border-slate-100 pt-2">
        {stageRequests.map(request => <div key={request.id} className="space-y-1.5">
          {stageRequests.length > 1 && <p className="flex flex-wrap justify-between gap-1 font-medium text-slate-700"><span>{statusLabel(request.status, zh)} · {fmtDT(request.submittedAt || request.createdAt)}</span><span className="tabular-nums">{money(request.amount)}</span></p>}
          {request.history?.length ? <ol className="space-y-1.5">{request.history.map((entry, index) => <li key={`${entry.at || ""}-${index}`} className="break-words leading-relaxed text-slate-600"><span className="font-medium">{actionLabel(entry.action, zh)}</span>{entry.by && <span> · {entry.by}</span>}{entry.at && <span className="text-slate-400"> · {fmtDT(entry.at)}</span>}{entry.note && <p className="mt-0.5 whitespace-pre-wrap text-slate-500">{entry.note}</p>}</li>)}</ol> : <p className="text-slate-500">{request.rejectReason || request.voidReason || (zh ? "暂无详细记录" : "No detailed history")}</p>}
        </div>)}
      </div>
    </details>}
  </div>;
}
